/**
 * Visual theme state for CloudLens.
 *
 * Deliberately the same four ids, the same two localStorage keys and the same
 * DOM contract as the main CloudForge app (frontend/lib/visual-theme.ts).
 * CloudLens is served from cloudforge.cloud/lens — the same origin as
 * CloudForge — so localStorage is shared: a theme picked in one product is
 * already in effect when you cross into the other. Renaming a key here would
 * silently break that, which is why the values are spelled out rather than
 * imported from a config.
 *
 * Difference from CloudForge: it writes `data-visual-theme` on <html> rather
 * than a `theme-<id>` class. CloudLens colours come from the MUI palette, so
 * the attribute exists for the --ds-* custom properties in themes.css, not for
 * a Tailwind class cascade.
 */

export type VisualTheme = 'paper' | 'classic' | 'blueprint' | 'contrast';

export const VISUAL_THEMES: readonly VisualTheme[] = ['paper', 'classic', 'blueprint', 'contrast'];

export const VISUAL_THEME_KEY = 'cf_visual_theme';
export const PRESENTATION_MODE_KEY = 'cf_presentation_mode';

export const DEFAULT_VISUAL_THEME: VisualTheme = 'paper';

export function normaliseTheme(value: string | null | undefined): VisualTheme {
  return VISUAL_THEMES.includes(value as VisualTheme) ? (value as VisualTheme) : DEFAULT_VISUAL_THEME;
}

export function getVisualTheme(): VisualTheme {
  if (typeof window === 'undefined') return DEFAULT_VISUAL_THEME;
  try {
    return normaliseTheme(window.localStorage.getItem(VISUAL_THEME_KEY));
  } catch {
    // Private mode and locked-down enterprise browsers throw on localStorage.
    return DEFAULT_VISUAL_THEME;
  }
}

export function saveVisualTheme(theme: VisualTheme): void {
  applyVisualTheme(theme);
  try {
    window.localStorage.setItem(VISUAL_THEME_KEY, theme);
  } catch {
    /* Choice is still applied for this session; it just will not survive a reload. */
  }
}

export function applyVisualTheme(theme: VisualTheme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.visualTheme = theme;
}

export function getPresentationMode(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(PRESENTATION_MODE_KEY) === '1';
  } catch {
    return false;
  }
}

export function savePresentationMode(on: boolean): void {
  applyPresentationMode(on);
  try {
    window.localStorage.setItem(PRESENTATION_MODE_KEY, on ? '1' : '0');
  } catch {
    /* As above. */
  }
}

export function applyPresentationMode(on: boolean): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('presentation-mode', on);
}

/**
 * Reads a --ds-* custom property as a concrete value.
 *
 * Needed because a few consumers hand DS colours to libraries that do not run
 * them through CSS — Recharts writes `stroke` as an SVG attribute — where a
 * raw `var(--ds-accent)` string would not resolve.
 */
export function resolveDsColor(cssVar: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim();
  return value || fallback;
}
